import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  DotsThreeVertical, Copy, Check, PaperPlaneTilt, EnvelopeSimple,
  ArrowClockwise, Prohibit, MagnifyingGlass, LinkSimple,
} from '@phosphor-icons/react';
import { adminAPI } from '../../lib/api';
import ConfirmDialog from '../events/ConfirmDialog';

// Human label + tone for a derived invite status.
function statusMeta(derived) {
  switch (derived) {
    case 'pending':  return { label: 'Pending',  cls: 'bg-[#015e3f]/10 text-[#015e3f]' };
    case 'accepted': return { label: 'Accepted', cls: 'bg-[#6a766e]/10 text-[#6a766e]' };
    case 'expired':  return { label: 'Expired',  cls: 'bg-[#fde7e7] text-[#9c2626]' };
    case 'canceled': return { label: 'Revoked',  cls: 'bg-[#f0ece3] text-[#8a6d12]' };
    default:         return { label: derived,    cls: 'bg-[#f0ece3] text-[#6a766e]' };
  }
}

// Collapse raw DB status + expiry into one of: pending | accepted | expired | canceled
function deriveStatus(inv) {
  if (inv.status === 'redeemed' || inv.used) return 'accepted';
  if (inv.status === 'canceled') return 'canceled';
  if (inv.status === 'expired') return 'expired';
  if (inv.status === 'pending') {
    if (inv.expires_at && inv.expires_at < new Date().toISOString()) return 'expired';
    return 'pending';
  }
  return inv.status || 'pending';
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'expired', label: 'Expired' },
  { id: 'canceled', label: 'Revoked' },
];

export default function InvitesPanel() {
  const [invites, setInvites] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sendMethod, setSendMethod] = useState('none');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [menuFor, setMenuFor] = useState(null);     // invite id whose menu is open
  const [confirm, setConfirm] = useState(null);     // { kind:'revoke'|'regenerate', inv }
  const [busy, setBusy] = useState(false);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await adminAPI.getInvites();
      setInvites(res.data.invites || []);
    } catch {
      toast.error('Could not load invitations');
    }
  }, []);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const needsPhone = sendMethod === 'sms' || sendMethod === 'both';
  const needsEmail = sendMethod === 'email' || sendMethod === 'both';
  const phoneInvalid = needsPhone && phone.trim().replace(/\D/g, '').length < 10;
  const emailInvalid = needsEmail && !email.trim();

  const handleCreate = async (e) => {
    e.preventDefault();
    if (phoneInvalid) { toast.error('Phone number is required for SMS invites.'); return; }
    if (emailInvalid) { toast.error('Email is required for email invites.'); return; }
    setCreating(true);
    try {
      const payload = {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        send_method: sendMethod,
        name: `${firstName.trim()} ${lastName.trim()}`.trim() || null,
      };
      const res = await adminAPI.createInvite(payload);
      const d = res.data?.dispatch || {};
      const parts = ['Invite created'];
      if (d.sms?.requested) parts.push(d.sms.status === 'sent' ? 'SMS sent' : `SMS ${d.sms.status}`);
      if (d.email?.requested) parts.push(d.email.status === 'sent' ? 'Email sent' : 'Email — copy link');
      toast.success(parts.join(' • '));
      setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setSendMethod('none');
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text, key, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
    setMenuFor(null);
  };

  const resend = async (inv, method) => {
    setMenuFor(null);
    try {
      const r = await adminAPI.resendInvite(inv.id, method);
      const chan = method === 'email' ? r.data?.dispatch?.email : r.data?.dispatch?.sms;
      if (chan?.status === 'sent' || chan?.status === 'dry_run' || chan?.status === 'off') {
        toast.success(`${method === 'email' ? 'Email' : 'SMS'} ${chan.status}${chan.to ? ` to ${chan.to}` : ''}`);
      } else {
        toast.warning(`${method === 'email' ? 'Email' : 'SMS'}: ${chan?.reason || chan?.status || 'not sent'}`);
      }
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'revoke') {
        await adminAPI.revokeInvite(confirm.inv.id);
        toast.success('Invitation revoked.');
      } else {
        const r = await adminAPI.regenerateInvite({ email: confirm.inv.email, send_method: 'none' });
        if (r?.data?.invite_url) {
          try { await navigator.clipboard.writeText(r.data.invite_url); } catch { /* ignore */ }
          toast.success('New invite generated & link copied.');
        } else {
          toast.success('New invite generated.');
        }
      }
      await fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: invites.length, pending: 0, accepted: 0, expired: 0, canceled: 0 };
    invites.forEach((inv) => { const d = deriveStatus(inv); c[d] = (c[d] || 0) + 1; });
    return c;
  }, [invites]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return invites
      .map((inv) => ({ ...inv, _derived: deriveStatus(inv) }))
      .filter((inv) => filter === 'all' ? true : inv._derived === filter)
      .filter((inv) => !term
        || (inv.invite_code || '').toLowerCase().includes(term)
        || (inv.email || '').toLowerCase().includes(term)
        || (inv.full_name || inv.name || '').toLowerCase().includes(term));
  }, [invites, q, filter]);

  return (
    <div className="space-y-4" data-testid="admin-invites-panel">
      {/* Create form */}
      <form onSubmit={handleCreate} className="space-y-2 bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Create invitation</div>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="invite-first-name-input" type="text" value={firstName}
            onChange={(e) => setFirstName(e.target.value)} placeholder="First Name"
            className="bg-white border border-[#e3dcd1] rounded-xl px-4 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]" />
          <input data-testid="invite-last-name-input" type="text" value={lastName}
            onChange={(e) => setLastName(e.target.value)} placeholder="Last Name"
            className="bg-white border border-[#e3dcd1] rounded-xl px-4 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f]" />
        </div>
        <input data-testid="invite-email-input" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder={`Email${needsEmail ? ' (required)' : ''}`}
          className={`w-full bg-white border rounded-xl px-4 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 ${emailInvalid ? 'border-[#b03a2e]' : 'border-[#e3dcd1] focus:border-[#015e3f]'}`} />
        <input data-testid="invite-phone-input" type="tel" value={phone}
          onChange={(e) => setPhone(e.target.value)} placeholder={`Phone${needsPhone ? ' (required)' : ' (optional)'}`}
          className={`w-full bg-white border rounded-xl px-4 py-2.5 text-sm text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 ${phoneInvalid ? 'border-[#b03a2e]' : 'border-[#e3dcd1] focus:border-[#015e3f]'}`} />
        <div className="pt-1">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-1.5">Send method</div>
          <div className="grid grid-cols-4 gap-1 bg-[#f0ece3] p-1 rounded-xl" data-testid="invite-send-method">
            {[{ id: 'none', label: 'Create only' }, { id: 'sms', label: 'SMS' }, { id: 'email', label: 'Email' }, { id: 'both', label: 'Both' }].map((opt) => (
              <button key={opt.id} type="button" data-testid={`invite-send-method-${opt.id}`}
                onClick={() => setSendMethod(opt.id)}
                className={`px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all ${sendMethod === opt.id ? 'bg-[#fbfaf8] text-[#015e3f] shadow-sm' : 'text-[#6a766e] hover:text-[#013523]'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <button data-testid="create-invite-button" type="submit" disabled={creating || phoneInvalid || emailInvalid}
          className="w-full bg-[#015e3f] text-white rounded-xl px-4 py-2.5 text-sm font-medium active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
          {creating ? 'Creating…' : 'Create Invite'}
        </button>
      </form>

      {/* Search + status filter */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex gap-1.5 overflow-x-auto scrollbar-thin" data-testid="invites-filters">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`invite-filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all border inline-flex items-center gap-1.5 ${filter === f.id ? 'bg-[#013523] text-white border-[#013523]' : 'bg-white text-[#6a766e] border-[#e3dcd1] hover:border-[#013523]/40 hover:text-[#013523]'}`}>
              {f.label}
              <span data-testid={`invite-filter-count-${f.id}`}
                className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-semibold ${filter === f.id ? 'bg-white/20 text-white' : 'bg-[#f0ece3] text-[#6a766e]'}`}>
                {counts[f.id] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="relative shrink-0">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
          <input data-testid="invites-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search code, name, email…"
            className="pl-8 pr-3 py-2 text-xs border border-[#e3dcd1] rounded-full w-full md:w-72 focus:outline-none focus:border-[#013523]" />
        </div>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="invites-list">
        {rows.length === 0 ? (
          <div className="bg-white border border-[#e3dcd1] rounded-2xl p-8 text-center text-sm text-[#6a766e]">
            No invitations match this view.
          </div>
        ) : rows.map((inv) => {
          const derived = inv._derived;
          const meta = statusMeta(derived);
          const shareLink = `${window.location.origin}/onboard?code=${inv.invite_code}`;
          const isValid = derived === 'pending';
          const isAccepted = derived === 'accepted';
          return (
            <div key={inv.id || inv.invite_code} data-testid={`invite-row-${inv.invite_code}`}
                 className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[#013523] font-medium" data-testid={`invite-code-${inv.invite_code}`}>
                      {inv.invite_code}
                    </span>
                    <span data-testid={`invite-status-${inv.invite_code}`} className={`text-[10px] px-1.5 py-0.5 rounded-full ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </div>
                  {(inv.full_name || inv.name) && <div className="text-xs text-[#2a332c] mt-0.5">{inv.full_name || inv.name}</div>}
                  {inv.email && <div className="text-xs text-[#6a766e]">{inv.email}</div>}
                  {isValid && (
                    <div className="mt-2 flex items-center gap-2 bg-white rounded-xl border border-[#e3dcd1] px-3 py-1.5 max-w-md">
                      <LinkSimple size={13} className="text-[#6a766e] shrink-0" />
                      <span className="flex-1 min-w-0 text-[11px] font-mono text-[#6a766e] truncate" title={shareLink}>{shareLink}</span>
                    </div>
                  )}
                </div>

                {/* Actions menu — accepted invites are read-only (no menu). */}
                {!isAccepted && (
                  <div className="relative shrink-0">
                    <button data-testid={`invite-menu-${inv.invite_code}`} onClick={() => setMenuFor(menuFor === inv.id ? null : inv.id)}
                      className="p-1.5 rounded-lg text-[#6a766e] hover:bg-[#f0ece3] transition-colors" title="Actions">
                      <DotsThreeVertical size={18} weight="bold" />
                    </button>
                    {menuFor === inv.id && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setMenuFor(null)} />
                        <div data-testid={`invite-menu-panel-${inv.invite_code}`}
                             className="absolute right-0 top-9 z-50 w-56 bg-white border border-[#e3dcd1] rounded-xl shadow-lg overflow-hidden text-left">
                          {isValid && (
                            <>
                              <MenuItem icon={<PaperPlaneTilt size={14} weight="bold" />} label="Resend via SMS"
                                        testid={`invite-resend-sms-${inv.invite_code}`} onClick={() => resend(inv, 'sms')} />
                              <MenuItem icon={<EnvelopeSimple size={14} weight="bold" />} label="Resend via Email"
                                        testid={`invite-resend-email-${inv.invite_code}`} onClick={() => resend(inv, 'email')} />
                              <MenuDivider />
                              <MenuItem icon={<LinkSimple size={14} weight="bold" />} label="Copy invite link"
                                        testid={`invite-copy-link-${inv.invite_code}`} onClick={() => copy(shareLink, `link-${inv.invite_code}`, 'Invite link')}
                                        trailing={copied === `link-${inv.invite_code}` ? <Check size={14} className="text-[#015e3f]" /> : null} />
                              <MenuItem icon={<Copy size={14} weight="bold" />} label="Copy invite code"
                                        testid={`invite-copy-code-${inv.invite_code}`} onClick={() => copy(inv.invite_code, `code-${inv.invite_code}`, 'Invite code')}
                                        trailing={copied === `code-${inv.invite_code}` ? <Check size={14} className="text-[#015e3f]" /> : null} />
                              <MenuDivider />
                            </>
                          )}
                          <MenuItem icon={<ArrowClockwise size={14} weight="bold" />} label="Regenerate invite"
                                    testid={`invite-regenerate-${inv.invite_code}`}
                                    onClick={() => { setMenuFor(null); setConfirm({ kind: 'regenerate', inv }); }} />
                          {isValid && (
                            <MenuItem icon={<Prohibit size={14} weight="bold" />} label="Revoke invite" tone="red"
                                      testid={`invite-revoke-${inv.invite_code}`}
                                      onClick={() => { setMenuFor(null); setConfirm({ kind: 'revoke', inv }); }} />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title={confirm?.kind === 'revoke' ? 'Revoke this invitation?' : 'Regenerate invitation?'}
        description={confirm?.kind === 'revoke'
          ? `Invite ${confirm?.inv?.invite_code} will be invalidated. The link/code will stop working, but its history is kept. You can create a new invite later.`
          : `A brand-new code will be minted for ${confirm?.inv?.email || 'this member'} and the current code (${confirm?.inv?.invite_code}) will be canceled. The new link is copied to your clipboard.`}
        confirmLabel={confirm?.kind === 'revoke' ? 'Revoke' : 'Regenerate'}
        destructive={confirm?.kind === 'revoke'}
        busy={busy}
        onConfirm={runConfirm}
      />
    </div>
  );
}

function MenuItem({ icon, label, onClick, disabled, testid, tone = 'default', trailing = null }) {
  const colors = tone === 'red' ? 'text-[#9c2626] hover:bg-[#fde7e7]' : 'text-[#013523] hover:bg-[#f0ece3]';
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] ${colors} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}>
      <span className="text-[#6a766e]">{icon}</span>
      <span className="truncate flex-1">{label}</span>
      {trailing}
    </button>
  );
}
function MenuDivider() { return <div className="h-px bg-[#e3dcd1]" />; }
